/**
 * Attendance V2 top-bar widget — Clock In / Break / End Break / Clock Out.
 * 2026-02 rebuild. Mounted in TaskManagerShell's top bar on every page.
 *
 * Reliability rules:
 *  - Location (GPS) is REQUIRED: on denial we show a clear warning and
 *    leave the current state untouched.
 *  - Backend errors surface as human-readable toasts; state re-syncs from
 *    the server afterwards (never guessed client-side).
 *  - No timers that clock anyone out. The only timer here re-renders the
 *    "On break · X min" label and re-polls status.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Clock, Coffee, LogOut, Play, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { attendanceV2API, eodV2API } from '../../lib/api';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { localDateStr, getRequiredLocation, fmtTime, STATUS_META } from '../../lib/attendanceV2';
import EodModal from '../eod/EodModal';

const POLL_MS = 60000;

export default function AttendanceWidget() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.id;
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [record, setRecord] = useState(null);
  const [staleRecord, setStaleRecord] = useState(null);
  const [busy, setBusy] = useState(false);
  // 2026-06 — ATOMIC EOD: Clock Out opens the End-of-Day modal FIRST; the
  // submit endpoint saves the report AND clocks out in one backend call.
  const [eodOpen, setEodOpen] = useState(false);
  const [eodTasks, setEodTasks] = useState([]);
  const [eodSubmitting, setEodSubmitting] = useState(false);
  const [, forceTick] = useState(0);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    if (!wsId) return;
    try {
      const res = await attendanceV2API.status(wsId, localDateStr());
      if (!mounted.current) return;
      setStatus(res.data.status || 'not_started');
      setRecord(res.data.record || null);
      setStaleRecord(res.data.stale_record || null);
    } catch {
      // Silent — widget keeps last known state; next poll retries.
    }
  }, [wsId]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const poll = setInterval(refresh, POLL_MS);
    const tick = setInterval(() => forceTick((n) => n + 1), 30000); // break-minutes label
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      mounted.current = false;
      clearInterval(poll); clearInterval(tick);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  const act = async (kind) => {
    if (busy || !wsId) return;
    const labels = {
      clockIn: 'clock in', breakStart: 'start your break',
      breakEnd: 'end your break', clockOut: 'clock out',
    };
    setBusy(true);
    try {
      const location = await getRequiredLocation(labels[kind]);
      if (!location) {
        // Warning toast already shown by getRequiredLocation.
        throw Object.assign(new Error('location_required'), { handled: true });
      }
      const payload = {
        workspace_id: wsId, local_date: localDateStr(), location,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      const call = {
        clockIn: attendanceV2API.clockIn,
        breakStart: attendanceV2API.breakStart,
        breakEnd: attendanceV2API.breakEnd,
        clockOut: attendanceV2API.clockOut,
      }[kind];
      const res = await call(payload);
      setRecord(res.data.record || null);
      setStatus(res.data.record?.status || 'not_started');
      // Nudge listeners (Team Pulse dashboard card) to re-fetch instantly.
      try { window.dispatchEvent(new Event('stellar-clock-changed')); } catch { /* noop */ }
      const done = {
        clockIn: res.data.resumed ? 'Welcome back — session resumed' : 'Clocked in',
        breakStart: 'Break started', breakEnd: 'Break ended', clockOut: 'Clocked out',
      }[kind];
      toast.success(done);
    } catch (e) {
      if (!e?.handled) {
        const msg = e?.response?.data?.detail
          || `Could not ${labels[kind]}, please try again.`;
        toast.error(msg);
        refresh(); // re-sync true state, never corrupt local state
      }
      throw e;
    } finally {
      setBusy(false);
    }
  };

  /** Clock Out entry point — EOD comes first, then the real clock-out. */
  const startClockOut = async () => {
    if (busy || !wsId) return;
    if (!(status === 'clocked_in' || status === 'on_break')) {
      toast.error('You are not clocked in today');
      return;
    }
    setBusy(true);
    try {
      const today = localDateStr();
      const st = await eodV2API.todayStatus(wsId, today);
      if (st.data.submitted) {
        // EOD already saved earlier (e.g. clock-out failed after save) —
        // go straight to clock-out.
        setBusy(false);
        await act('clockOut').catch(() => {});
        return;
      }
      const tt = await eodV2API.tasksTouched(wsId, today, new Date().getTimezoneOffset());
      setEodTasks(tt.data.tasks || []);
      setEodOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load your End of Day report, please try again.');
    } finally {
      setBusy(false);
    }
  };

  /** EOD modal submit — ATOMIC: one backend call saves the report, closes
   *  any open break, and clocks the user out (clock-out time = submit time).
   *  2026-07-14 short-summary model: {summary (required), blockers, plan}. */
  const handleEodSubmit = async ({ summary, blockers, planTomorrow }) => {
    setEodSubmitting(true);
    try {
      const location = await getRequiredLocation('clock out');
      if (!location) return; // warning toast already shown; modal stays open
      const res = await eodV2API.submit({
        workspace_id: wsId,
        local_date: localDateStr(),
        tz_offset: new Date().getTimezoneOffset(),
        summary,
        blockers,
        plan_tomorrow: planTomorrow,
        location,
      });
      setRecord(res.data.record || null);
      setStatus(res.data.record?.status || 'clocked_out');
      setEodOpen(false);
      try { window.dispatchEvent(new Event('stellar-clock-changed')); } catch { /* noop */ }
      toast.success('EOD saved — you are clocked out');
    } catch (e) {
      // Save failed — keep the modal (and all typed text) open.
      toast.error(e?.response?.data?.detail || 'Could not save your EOD report, please try again.');
      refresh(); // re-sync true state from the server
    } finally {
      setEodSubmitting(false);
    }
  };

  if (!wsId || status === 'loading') return null;

  // ── status label ──────────────────────────────────────────────────
  const meta = STATUS_META[status] || STATUS_META.not_started;
  let label = meta.label;
  if (status === 'clocked_in' && record?.clock_in_time) {
    label = `Clocked in since ${fmtTime(record.clock_in_time)}`;
  } else if (status === 'on_break') {
    const open = (record?.breaks || []).find((b) => !b.break_end_time);
    if (open?.break_start_time) {
      const mins = Math.max(0, Math.round((Date.now() - new Date(open.break_start_time).getTime()) / 60000));
      label = `On break · ${mins} min`;
    }
  }
  const toneCls = {
    muted: 'bg-foreground/[0.06] text-muted-foreground',
    green: 'bg-emerald-500/15 text-emerald-400',
    amber: 'bg-amber-500/15 text-amber-400',
    blue: 'bg-sky-500/15 text-sky-400',
  }[meta.tone];

  const btn = 'h-8 text-[11px] font-medium px-2.5';

  return (
    <div className="flex items-center gap-1.5" data-testid="av2-widget">
      {/* 2026-07-31 — "Fix {date}" dev/debug button removed per owner request.
          The stale clock-out record logic stays intact on the Attendance page. */}
      <span
        className={`hidden sm:inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full ${toneCls}`}
        data-testid="av2-status-badge"
      >
        <Clock className="h-3 w-3" /> {label}
      </span>
      {(status === 'not_started' || status === 'clocked_out') && (
        <Button size="sm" disabled={busy} onClick={() => act('clockIn').catch(() => {})} data-testid="av2-clock-in-btn"
          className={`${btn} bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 border border-emerald-500/30`}>
          <Play className="h-3 w-3 mr-1" /> Clock In
        </Button>
      )}
      {status === 'clocked_in' && (
        <>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => act('breakStart').catch(() => {})} data-testid="av2-break-btn"
            className={`${btn} text-amber-400 hover:bg-amber-500/15`}>
            <Coffee className="h-3 w-3 mr-1" /> Break
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={startClockOut} data-testid="av2-clock-out-btn"
            className={`${btn} text-red-400 hover:bg-red-500/15`}>
            <LogOut className="h-3 w-3 mr-1" /> Clock Out
          </Button>
        </>
      )}
      {status === 'on_break' && (
        <>
          <Button size="sm" disabled={busy} onClick={() => act('breakEnd').catch(() => {})} data-testid="av2-endbreak-btn"
            className={`${btn} bg-amber-500/15 hover:bg-amber-500/25 text-amber-400 border border-amber-500/30`}>
            <Play className="h-3 w-3 mr-1" /> End Break
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={startClockOut} data-testid="av2-clock-out-btn"
            className={`${btn} text-red-400 hover:bg-red-500/15`}>
            <LogOut className="h-3 w-3 mr-1" /> Clock Out
          </Button>
        </>
      )}
      <EodModal
        open={eodOpen}
        onOpenChange={(v) => { if (!eodSubmitting) setEodOpen(v); }}
        tasks={eodTasks}
        submitting={eodSubmitting}
        onSubmit={handleEodSubmit}
      />
    </div>
  );
}
